<template>
  <Dialog
    :visible="show"
    @update:visible="$emit('close')"
    :modal="true"
    class="p-fluid"
    :style="{ width: '50vw' }"
    :header="profile ? 'Editar Perfil' : 'Novo Perfil'"
    :closable="true"
  >
    <div class="space-y-6">
      <!-- Informações Básicas -->
      <div class="grid grid-cols-2 gap-4">
        <div class="field col-span-2">
          <label for="name" class="font-medium">Nome do Perfil</label>
          <InputText
            id="name"
            v-model="form.name"
            :class="{ 'p-invalid': v$.name.$error }"
            aria-describedby="name-error"
          />
          <small v-if="v$.name.$error" id="name-error" class="p-error">
            {{ v$.name.$errors[0].$message }}
          </small>
        </div>

        <div class="field col-span-2">
          <label for="description" class="font-medium">Descrição</label>
          <Textarea
            id="description"
            v-model="form.description"
            rows="3"
            placeholder="Descreva o propósito deste perfil e suas principais responsabilidades"
            :class="{ 'p-invalid': v$.description.$error }"
            aria-describedby="description-error"
          />
          <small v-if="v$.description.$error" id="description-error" class="p-error">
            {{ v$.description.$errors[0].$message }}
          </small>
        </div>

        <div class="field">
          <label for="color" class="font-medium">Cor</label>
          <Dropdown
            id="color"
            v-model="form.color"
            :options="colorOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Selecione uma cor"
            :class="{ 'p-invalid': v$.color.$error }"
          >
            <template #value="slotProps">
              <div v-if="slotProps.value" class="flex items-center">
                <div :class="['w-4 h-4 rounded mr-2', getColorClass(slotProps.value)]"></div>
                <div>{{ getColorLabel(slotProps.value) }}</div>
              </div>
              <span v-else>{{ slotProps.placeholder }}</span>
            </template>
            <template #option="slotProps">
              <div class="flex items-center">
                <div :class="['w-4 h-4 rounded mr-2', getColorClass(slotProps.option.value)]"></div>
                <div>{{ slotProps.option.label }}</div>
              </div>
            </template>
          </Dropdown>
        </div>

        <div class="field">
          <label for="icon" class="font-medium">Ícone</label>
          <Dropdown
            id="icon"
            v-model="form.icon"
            :options="iconOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Selecione um ícone"
            :class="{ 'p-invalid': v$.icon.$error }"
          >
            <template #value="slotProps">
              <div v-if="slotProps.value" class="flex items-center">
                <i :class="['pi', slotProps.value, 'mr-2']"></i>
                <div>{{ getIconLabel(slotProps.value) }}</div>
              </div>
              <span v-else>{{ slotProps.placeholder }}</span>
            </template>
            <template #option="slotProps">
              <div class="flex items-center">
                <i :class="['pi', slotProps.option.value, 'mr-2']"></i>
                <div>{{ slotProps.option.label }}</div>
              </div>
            </template>
          </Dropdown>
        </div>
      </div>

      <!-- Permissões de Menu -->
      <div class="mt-6">
        <h3 class="text-lg font-medium mb-4">Permissões de Menu</h3>
        
        <!-- Menu Principal -->
        <div class="mb-6">
          <h4 class="text-sm font-medium text-gray-700 mb-3">Menu Principal</h4>
          <div class="space-y-3">
            <div
              v-for="item in menuItems"
              :key="getPermissionKey(item)"
              class="flex items-center gap-2"
            >
              <Checkbox
                v-model="form.menuPermissions[getPermissionKey(item)]"
                :binary="true"
              />
              <i :class="['pi', item.icon]"></i>
              <span>{{ item.name }}</span>
            </div>
          </div>
        </div>

        <!-- Configurações da Organização -->
        <div class="mb-6">
          <h4 class="text-sm font-medium text-gray-700 mb-3">Configurações da Organização</h4>
          <div class="space-y-3">
            <div
              v-for="item in organizationSettings"
              :key="item.path"
              class="flex items-center gap-2"
            >
              <Checkbox
                v-model="form.menuPermissions[item.path]"
                :binary="true"
              />
              <i :class="['pi', item.icon]"></i>
              <span>{{ item.name }}</span>
            </div>
          </div>
        </div>

        <!-- Configurações Gerais -->
        <div>
          <h4 class="text-sm font-medium text-gray-700 mb-3">Configurações Gerais</h4>
          <div class="space-y-3">
            <div
              v-for="item in generalSettings"
              :key="item.path"
              class="flex items-center gap-2"
            >
              <Checkbox
                v-model="form.menuPermissions[item.path]"
                :binary="true"
              />
              <i :class="['pi', item.icon]"></i>
              <span>{{ item.name }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end gap-2">
        <Button
          label="Cancelar"
          icon="pi pi-times"
          class="p-button-text"
          @click="$emit('close')"
        />
        <Button
          label="Salvar"
          icon="pi pi-check"
          @click="handleSubmit"
        />
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, reactive, watch, onMounted } from 'vue'
import { useVuelidate } from '@vuelidate/core'
import { required, minLength } from '@vuelidate/validators'
import { useProfileStore } from '@/stores/profile'
import type { Profile } from '@/firebase/services/profiles.service'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Textarea from 'primevue/textarea'
import Dropdown from 'primevue/dropdown'
import Checkbox from 'primevue/checkbox'

const props = defineProps<{
  show: boolean
  profile: Profile
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'saved'): void
}>()

const form = reactive({
  name: '',
  description: '',
  permissions: {} as { [key: string]: boolean },
  menuPermissions: {} as { [key: string]: boolean },
  color: '',
  icon: ''
})

const rules = {
  name: { required, minLength: minLength(3) },
  description: { required },
  color: { required },
  icon: { required },
  menuPermissions: { required }
}

const v$ = useVuelidate(rules, form)

const profileStore = useProfileStore()

// Menu items
const menuItems = [
  { 
    path: '/dashboard', 
    name: 'Dashboard', 
    icon: 'pi-home', 
    description: 'Painel principal com visão geral do sistema' 
  },
  { 
    path: '/accounts', 
    name: 'Contas a Pagar e Receber', 
    icon: 'pi-money-bill', 
    description: 'Gerenciamento de contas e transações' 
  },
  { 
    path: '/cash-flow', 
    name: 'Fluxo de Caixa', 
    icon: 'pi-chart-line', 
    description: 'Relatórios e análises financeiras' 
  },
  {
    path: 'https://bhissdigital.pbh.gov.br/nfse/',
    name: 'Prefeitura de BH',
    icon: 'pi-external-link',
    description: 'Portal de Notas Fiscais da Prefeitura de BH',
    external: true
  },
  {
    path: 'https://contadigital.inter.co/',
    name: 'Banco Inter',
    icon: 'pi-credit-card',
    description: 'Internet Banking do Banco Inter',
    external: true
  }
]

// Configurações da Organização
const organizationSettings = [
  { 
    path: '/banks', 
    name: 'Bancos', 
    icon: 'pi-building', 
    description: 'Configurações de bancos' 
  },
  { 
    path: '/suppliers', 
    name: 'Fornecedores', 
    icon: 'pi-briefcase', 
    description: 'Gerenciamento de fornecedores' 
  },
  { 
    path: '/customers', 
    name: 'Clientes', 
    icon: 'pi-users', 
    description: 'Gerenciamento de clientes' 
  },
  { 
    path: '/cost-centers', 
    name: 'Centro de Custos', 
    icon: 'pi-chart-pie', 
    description: 'Gerenciamento de centro de custos' 
  },
  { 
    path: '/payment-status', 
    name: 'Status de Pagamento', 
    icon: 'pi-tag', 
    description: 'Configuração de status de pagamento' 
  }
]

// Configurações Gerais
const generalSettings = [
  { 
    path: '/profiles', 
    name: 'Perfis de Acesso', 
    icon: 'pi-users', 
    description: 'Gerenciamento de perfis de acesso' 
  },
  { 
    path: '/my-account', 
    name: 'Minha Conta', 
    icon: 'pi-user', 
    description: 'Configurações da conta' 
  },
  { 
    path: '/settings', 
    name: 'Configurações', 
    icon: 'pi-cog', 
    description: 'Configurações gerais do sistema' 
  }
]

const colorOptions = [
  { label: 'Azul', value: 'blue' },
  { label: 'Verde', value: 'green' },
  { label: 'Vermelho', value: 'red' },
  { label: 'Amarelo', value: 'yellow' },
  { label: 'Roxo', value: 'purple' }
]

const iconOptions = [
  { label: 'Usuário', value: 'pi-user' },
  { label: 'Grupo', value: 'pi-users' },
  { label: 'Engrenagem', value: 'pi-cog' },
  { label: 'Estrela', value: 'pi-star' },
  { label: 'Chave', value: 'pi-key' }
]

// Função para obter a chave de permissão do item
const getPermissionKey = (item: any) => item.external ? item.name : item.path

// Ao montar o componente
onMounted(() => {
  if (props.profile) {
    // Copiar os dados do perfil
    form.name = props.profile.name
    form.description = props.profile.description
    form.icon = props.profile.icon
    form.color = props.profile.color

    // Inicializar as permissões
    menuItems.forEach(item => {
      const key = getPermissionKey(item)
      form.menuPermissions[key] = props.profile.menuPermissions?.[key] || false
    })

    organizationSettings.forEach(item => {
      form.menuPermissions[item.path] = props.profile.menuPermissions?.[item.path] || false
    })

    generalSettings.forEach(item => {
      form.menuPermissions[item.path] = props.profile.menuPermissions?.[item.path] || false
    })
  }
})

const handleSubmit = async () => {
  const isValid = await v$.value.$validate()
  if (!isValid) return

  try {
    const success = await profileStore.updateProfile(props.profile.id!, {
      name: form.name,
      description: form.description,
      permissions: form.permissions,
      menuPermissions: form.menuPermissions,
      color: form.color,
      icon: form.icon
    })

    if (success) {
      emit('saved')
      emit('close')
    }
  } catch (error) {
    console.error('Error updating profile:', error)
  }
}

const getColorClass = (color: string) => {
  const colors = {
    blue: 'bg-blue-100',
    green: 'bg-green-100',
    purple: 'bg-purple-100',
    red: 'bg-red-100',
    yellow: 'bg-yellow-100'
  }
  return colors[color as keyof typeof colors]
}

const getColorLabel = (value: string) => {
  return colorOptions.find(option => option.value === value)?.label || value
}

const getIconLabel = (value: string) => {
  return iconOptions.find(option => option.value === value)?.label || value
}
</script>
